import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { TestRun } from '@/types/testrun.types'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    testRun?: TestRun
    mode: 'create' | 'edit'
}

const TestRunDialog = ({
                           open,
                           onOpenChange,
                           testRun,
                           mode,
                       }: Props) => {
    const queryClient = useQueryClient()

    const [name, setName] = useState('')
    const [notes, setNotes] = useState('')
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (mode === 'edit' && testRun) {
            setName(testRun.name)
            setNotes(testRun.notes ?? '')
        } else {
            setName('')
            setNotes('')
        }
    }, [mode, testRun, open])

    const handleSubmit = async () => {
        try {
            setLoading(true)

            if (mode === 'create') {
                await apiClient.post('/api/testruns', {
                    name,
                    notes,
                })
                toast.success('Test run created successfully')
            } else if (mode === 'edit' && testRun) {
                await apiClient.put(`/api/testruns/${testRun.test_run_id}`, {
                    name,
                    notes,
                })
                toast.success('Test run updated successfully')
            }

            queryClient.invalidateQueries({ queryKey: ['testRuns'] })
            onOpenChange(false)
        } catch (error) {
            toast.error('Failed to save test run')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {mode === 'create' ? 'Create Test Run' : 'Edit Test Run'}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <label className="text-sm font-medium">Name</label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Test run name"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium">Notes</label>
                        <Input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Optional notes"
                        />
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleSubmit} disabled={loading || !name}>
                            {loading
                                ? 'Saving...'
                                : mode === 'create'
                                    ? 'Create'
                                    : 'Update'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export { TestRunDialog }
